import axios from 'axios';
import Cookies from 'js-cookie';
import { getAuth, signOut, signInWithCustomToken } from 'firebase/auth';
import firebaseApp from 'config/firebase';

const auth = getAuth(firebaseApp);

const instance = axios.create({
    baseURL: process.env.SERVER_BASE_URL, 
    withCredentials: true, 
    headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': Cookies.get('csrftoken')
    },
});

instance.interceptors.request.use(async config => {
    config.headers.Authorization = auth.currentUser
        ? await auth.currentUser.getIdToken()
        : null;

    return config;
}, error => {
    return Promise.reject(error);
});

let endingSession = false;
let ownLoginChangeUntil = 0;

// Changing a tutor's own email ends their Firebase session; the server sends back a
// custom token for a fresh one. Sign in with it, and for a short window ignore
// session_revoked from requests that were still in flight on the old token.
export const resumeOwnSession = (customToken: string) => {
    ownLoginChangeUntil = Date.now() + 15_000;
    return signInWithCustomToken(auth, customToken);
};

// The server answers 401 with this code when the request's login was revoked by a
// password or email change (server/Prestige/firebase_revocation.py). Tell the tutor
// why and sign out once; AuthContext then shows the sign-in page. Requests already in
// flight fail the same way after the sign-out, so the latch stays a few seconds to
// keep them from popping the alert again.
instance.interceptors.response.use(response => response, error => {
    const revoked = error?.response?.status === 401 && error.response.data?.code === 'session_revoked';

    if (revoked && !endingSession && Date.now() >= ownLoginChangeUntil) {
        endingSession = true;
        window.alert(error.response.data.message);
        signOut(auth).finally(() => {
            setTimeout(() => { endingSession = false; }, 5_000);
        });
    }

    return Promise.reject(error);
});

export default instance;